import { baseApi } from './baseApi';
import { FolderManagerItemActionNames, FolderManagerItemActionTypes } from '../interfaces';

const mapApiToStore = (item: any) => ({
  id: item.ID,
  displayName:
    item?.Data?.display_name ||
    item?.Data?.name ||
    item?.Data?.title ||
    `${item?.Data?.first_name} ${item?.Data?.last_name}`,
  type: item?.Type,
  actions: [],
  ...item,
});

export const entitiesService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEntitiesByType: builder.query<any[], { entityType: string }>({
      query: ({ entityType }) => `/entities/${entityType}`,
      transformResponse: (result: any) => result.data.map(mapApiToStore),
      providesTags: (result, error, { entityType }) => [{ type: 'Entities', id: entityType }],
    }),
    getEntityById: builder.query({
      query: ({ entityType, entityId }) => `/entities/${entityType}/${entityId}`,
      transformResponse: (result: any) => mapApiToStore(result.data),
      providesTags: (result, error, { entityType, entityId }) => [
        { type: 'Entities', id: `${entityType}-${entityId}` },
      ],
    }),
    addEntity: builder.mutation({
      query: ({ entityType, data }) => ({
        url: `/entities/${entityType}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { entityType }) => [{ type: 'Entities', id: entityType }],
    }),
    addEntityToCmsScope: builder.mutation({
      query: ({ cmsScopeId, entityType, data }) => ({
        url: `/cms-scopes/${cmsScopeId}/entities/${entityType}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { entityType, cmsScopeId }) => [
        { type: 'Entities', id: entityType },
        { type: 'Entities', id: `CMS_SCOPE_${cmsScopeId}_${entityType}` },
      ],
    }),
    createEdge: builder.mutation({
      query: ({ entityType, entityId, edgeType, data }) => ({
        url: `/entities/${entityType}/${entityId}/${edgeType}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { entityType, entityId }) => [
        { type: 'Entities', id: `${entityType}-${entityId}` },
      ],
    }),
    removeEdge: builder.mutation({
      query: ({ entityType, entityId, edgeType, data }) => ({
        url: `/entities/${entityType}/${entityId}/${edgeType}`,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: (result, error, { entityType, entityId }) => [
        { type: 'Entities', id: `${entityType}-${entityId}` },
      ],
    }),
    updateEntity: builder.mutation({
      query: ({ entityType, entityId, data }) => ({
        url: `/entities/${entityType}/${entityId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { entityType, entityId }) => [
        { type: 'Entities', id: `${entityType}-${entityId}` },
        { type: 'Entities', id: entityType },
      ],
    }),
    deleteEntity: builder.mutation({
      query: ({ entityType, entityId }) => ({
        url: `/entities/${entityType}/${entityId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { entityType, entityId }) => [
        { type: 'Entities', id: `${entityType}-${entityId}` },
        { type: 'Entities', id: entityType },
      ],
    }),
  }),
});

export const {
  useGetEntitiesByTypeQuery,
  useLazyGetEntitiesByTypeQuery,
  useGetEntityByIdQuery,
  useAddEntityMutation,
  useAddEntityToCmsScopeMutation,
  useRemoveEdgeMutation,
  useCreateEdgeMutation,
  useUpdateEntityMutation,
  useDeleteEntityMutation,
} = entitiesService;
